import React, { useState, useMemo } from "react"
import PropTypes from "prop-types"
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
} from "@mui/material"
import StarIcon from "@mui/icons-material/Star"
import StarBorderIcon from "@mui/icons-material/StarBorder"
import EditIcon from "@mui/icons-material/Edit"
import ArchiveIcon from "@mui/icons-material/Archive"
import AddIcon from "@mui/icons-material/Add"

import {
  useSeries,
  useArchiveSerie,
  useSetDefaultSerie,
} from "./hooks/useSeries"
import { buildSerieNextNumber } from "./buildSeriePreview"
import SerieFormModal from "./SerieFormModal"

const SeriesTab = ({ organizationId, serieType }) => {
  const [includeArchived, setIncludeArchived] = useState(false)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [actionError, setActionError] = useState(null)

  const {
    data: allSeries = [],
    isLoading,
    error,
  } = useSeries(organizationId, serieType)
  const archiveMutation = useArchiveSerie(organizationId, serieType)
  const setDefaultMutation = useSetDefaultSerie(organizationId, serieType)

  const visibleSeries = useMemo(
    () => (includeArchived ? allSeries : allSeries.filter(s => !s.archived)),
    [allSeries, includeArchived],
  )

  const rowBusySK =
    (archiveMutation.isPending && archiveMutation.variables) ||
    (setDefaultMutation.isPending && setDefaultMutation.variables) ||
    null

  const anyMutationInFlight =
    archiveMutation.isPending || setDefaultMutation.isPending

  const errorMessage =
    actionError || error?.response?.data?.detail || error?.message || null

  const handleNew = () => {
    setEditing(null)
    setFormOpen(true)
  }

  const handleEdit = serie => {
    setEditing(serie)
    setFormOpen(true)
  }

  const handleSetDefault = async serie => {
    setActionError(null)
    try {
      await setDefaultMutation.mutateAsync(serie.SK)
    } catch (e) {
      setActionError(
        e?.response?.data?.detail || e?.message || "Error al marcar default",
      )
    }
  }

  const handleArchive = async serie => {
    setActionError(null)
    try {
      await archiveMutation.mutateAsync(serie.SK)
    } catch (e) {
      setActionError(
        e?.response?.data?.detail || e?.message || "Error al archivar",
      )
    }
  }

  const labelFor = serie =>
    (serie.name && serie.name.trim()) ||
    buildSerieNextNumber(serie) ||
    "(sin nombre)"

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={includeArchived}
              onChange={e => setIncludeArchived(e.target.checked)}
              size="small"
            />
          }
          label="Ver archivadas"
          sx={{ ml: 1 }}
        />
        <Button
          variant="contained"
          color="branded"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleNew}
        >
          Nueva serie
        </Button>
      </Box>

      {errorMessage && (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          onClose={() => setActionError(null)}
        >
          {errorMessage}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : visibleSeries.length === 0 ? (
        <Box sx={{ textAlign: "center", color: "#888", py: 4 }}>
          No hay series creadas.
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={60} align="center">
                Default
              </TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Próximo N°</TableCell>
              <TableCell width={120}>Estado</TableCell>
              <TableCell width={140}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleSeries.map(s => {
              const busy = rowBusySK === s.SK
              const isArchived = s.archived
              const isExhausted = s.exhausted
              const isDefault = s.is_default
              const actionsDisabled = busy || anyMutationInFlight
              return (
                <TableRow
                  key={s.SK}
                  sx={{
                    opacity: isArchived || isExhausted ? 0.55 : 1,
                    "& .row-action": {
                      opacity: 0,
                      transition: "opacity 120ms ease-in",
                    },
                    "&:hover .row-action": { opacity: 1 },
                  }}
                >
                  <TableCell align="center">
                    {isArchived ? null : isDefault ? (
                      <Tooltip title="Serie por defecto">
                        <StarIcon fontSize="small" sx={{ color: "#f5b400" }} />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Marcar como default">
                        <span className="row-action">
                          <IconButton
                            size="small"
                            disabled={actionsDisabled || isExhausted}
                            onClick={() => handleSetDefault(s)}
                          >
                            <StarBorderIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{labelFor(s)}</TableCell>
                  <TableCell>
                    <code style={{ fontSize: 13 }}>
                      {buildSerieNextNumber(s)}
                    </code>
                  </TableCell>
                  <TableCell>
                    {isArchived ? (
                      <Chip size="small" label="Archivada" />
                    ) : isExhausted ? (
                      <Chip size="small" color="error" label="Agotada" />
                    ) : s.started ? (
                      <Chip size="small" color="primary" label="Iniciada" />
                    ) : (
                      <Chip
                        size="small"
                        variant="outlined"
                        label="No iniciada"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right" className="row-action">
                    {!isArchived && (
                      <>
                        <Tooltip title="Editar">
                          <span>
                            <IconButton
                              size="small"
                              disabled={busy}
                              onClick={() => handleEdit(s)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            isDefault
                              ? "No se puede archivar la serie default. Marca otra como default primero."
                              : anyMutationInFlight
                                ? "Esperando confirmación del servidor..."
                                : "Archivar"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={actionsDisabled || isDefault}
                              onClick={() => handleArchive(s)}
                            >
                              <ArchiveIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}

      <SerieFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        organizationId={organizationId}
        serieType={serieType}
        serie={editing}
      />
    </Box>
  )
}

SeriesTab.propTypes = {
  organizationId: PropTypes.string.isRequired,
  serieType: PropTypes.oneOf([
    "invoices",
    "correctives",
    "proformas",
    "purchase_orders",
  ]).isRequired,
}

export default SeriesTab
